"use client";

import React, { useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverlay,
    useDroppable,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Avatar, AvatarGroup, Box, Card, CardContent, Chip, Divider, Grid, Sheet, Typography } from "@mui/joy";
import { Circle } from "lucide-react";
import moment from "moment";

import { useKanbanBoard } from "./hooks";

type Task = {
    id: string;
    content: string;
    tags: string[];
    assignee: string;
    createdAt: string;
    lastUpdated: string;
};

type Column = {
    id: string;
    title: string;
    tasks: Task[];
};

const getRandomColor = () => {
    const colors = ["primary", "neutral", "danger", "success", "warning"];
    return colors[Math.floor(Math.random() * colors.length)];
};

interface IKanbanBoard {
    setKanbanTasks: any;
}

function KanbanBoard(props: IKanbanBoard) {
    const [activeTask, setActiveTask] = useState<Task | null>(null);
    const { setKanbanTasks } = { ...props };

    const { tasks, columns, setColumns, handleTaskDbUpdate } = useKanbanBoard();

    //console.log(kanbanTasks);

    const sensors = useSensors(useSensor(PointerSensor));

    const findTaskById = (id: string) => {
        for (const column of columns) {
            const task = column.tasks.find((t) => t.id === id);
            if (task) return { task, column };
        }
        return null;
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveTask(null);

        if (!over || active.id === over.id) return;

        const sourceInfo = findTaskById(active.id as string);
        const targetInfo = findTaskById(over.id as string);

        if (!sourceInfo) return;
        const sourceCol = sourceInfo.column;
        const taskToMove = sourceInfo.task;

        let updatedSourceTasks = sourceCol.tasks.filter((t) => t.id !== active.id);

        let updatedColumns = [...columns];

        if (targetInfo) {
            const targetCol = targetInfo.column;
            const targetIndex = targetCol.tasks.findIndex((t) => t.id === over.id);
            const updatedTargetTasks = [...targetCol.tasks];
            updatedTargetTasks.splice(targetIndex, 0, taskToMove);

            updatedColumns = updatedColumns.map((col) => {
                if (col.id === sourceCol.id) return { ...col, tasks: updatedSourceTasks };
                if (col.id === targetCol.id) return { ...col, tasks: updatedTargetTasks };
                return col;
            });
            console.log("now its here", targetInfo);
            const dbUpdateParams = { ...taskToMove.taskSummary, Status: targetCol.id as any };
            handleTaskDbUpdate(dbUpdateParams);
        } else {
            const targetCol = columns.find((col) => col.id === over.id);

            if (!targetCol) return;

            if (sourceCol.id === targetCol.id) return;

            const updatedTargetTasks = [...targetCol.tasks, taskToMove];

            updatedColumns = updatedColumns.map((col) => {
                if (col.id === sourceCol.id) return { ...col, tasks: updatedSourceTasks };
                if (col.id === targetCol.id) return { ...col, tasks: updatedTargetTasks };
                return col;
            });
            const dbUpdateParams = { ...taskToMove.taskSummary, Status: targetCol.id as any };
            handleTaskDbUpdate(dbUpdateParams);
        }

        // console.log(updatedColumns  );
        setColumns(updatedColumns);
        setKanbanTasks(updatedColumns);
    };

    return (
        <Box>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={(event) => {
                    const taskInfo = findTaskById(event.active.id as string);
                    if (taskInfo) setActiveTask(taskInfo.task);
                }}
            >
                <Grid container spacing={2}>
                    {columns.map((column) => (
                        <Grid key={column.id} xs={3}>
                            <DroppableColumn column={column}>
                                <SortableContext
                                    items={column.tasks.map((t) => t.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {column.tasks.map((task) => (
                                        <SortableTask key={task.id} task={task} />
                                    ))}
                                </SortableContext>
                            </DroppableColumn>
                        </Grid>
                    ))}
                </Grid>
                <DragOverlay>
                    {activeTask ? (
                        <Card variant="outlined" sx={{ width: "100%" }}>
                            <CardContent>
                                <Box>
                                    <Box display={"flex"} gap={1} mb={1}>
                                        {activeTask.tags.map((tag: string, idx: number) => (
                                            <Chip
                                                key={idx}
                                                variant="soft"
                                                color={getRandomColor() as any}
                                                sx={{
                                                    borderRadius: "6px",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                                size="sm"
                                            >
                                                {tag}
                                            </Chip>
                                        ))}
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography level="title-md">{activeTask.content}</Typography>
                                    <Typography level="body-xs" gutterBottom>
                                        Last updated {moment(activeTask.lastUpdated).fromNow()}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mt={1}>
                                    <Box>
                                        <Typography level="body-xs">
                                            Created {moment(activeTask.createdAt).fromNow()}
                                        </Typography>
                                    </Box>
                                    <AvatarGroup
                                        sx={{
                                            "--Avatar-size": "26px",
                                        }}
                                    >
                                        <Avatar
                                            alt={activeTask.assignee}
                                            src="/static/images/avatar/1.jpg"
                                            variant="outlined"
                                            color={getRandomColor() as any}
                                            sx={{ borderRadius: "6px", border: "1px solid", borderColor: "divider" }}
                                        />
                                    </AvatarGroup>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Box>
    );
}

function DroppableColumn({ column, children }: { column: Column; children: React.ReactNode }) {
    const { setNodeRef } = useDroppable({ id: column.id });

    return (
        <Sheet
            ref={setNodeRef}
            variant="plain"
            sx={{
                p: 0,
                borderRadius: "sm",
            }}
        >
            <Box
                sx={{
                    backgroundColor: "background.level2",
                    // position: "sticky",
                    // top: "135px",
                    px: 1.5,
                    py: 0.5,
                    mb: 1,
                    borderRadius: "7px",
                    opacity: 0.7,
                    display: "flex",
                    alignItems: "center",
                    zIndex: 1002,
                    gap: 1,
                }}
            >
                <Circle size={16} />
                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                    {column.title}
                </Typography>
            </Box>

            {children}
        </Sheet>
    );
}

function SortableTask({ task }: { task: Task }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: 1,
    };

    return (
        <Card
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            //variant="soft"
            sx={{ ...style, cursor: "grab", border: "1px solid", borderColor: "divider" }}
        >
            <CardContent>
                <Box>
                    <Box display={"flex"} gap={1} mb={1}>
                        {task.tags.map((tag: string, idx: number) => (
                            <Chip
                                key={idx}
                                variant="soft"
                                color={getRandomColor() as any}
                                sx={{ borderRadius: "6px", border: "1px solid", borderColor: "divider" }}
                                size="sm"
                            >
                                {tag}
                            </Chip>
                        ))}
                    </Box>
                </Box>
                <Box>
                    <Typography level="title-md">{task.content}</Typography>
                    <Typography level="body-xs" gutterBottom>
                        Last updated {moment(task.lastUpdated).fromNow()}
                    </Typography>
                </Box>
                <Divider />
                <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mt={1}>
                    <Box>
                        <Typography level="body-xs">Created {moment(task.createdAt).fromNow()}</Typography>
                    </Box>
                    <AvatarGroup
                        sx={{
                            "--Avatar-size": "26px",
                        }}
                    >
                        <Avatar
                            alt={task.assignee}
                            src="/static/images/avatar/1.jpg"
                            variant="outlined"
                            color={getRandomColor() as any}
                            sx={{ borderRadius: "6px", border: "1px solid", borderColor: "divider" }}
                        />
                    </AvatarGroup>
                </Box>
            </CardContent>
        </Card>
    );
}

export default KanbanBoard;
